import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { Menu, X, Globe } from "lucide-react";
import { COMPANY } from "@/i18n/translations";

const navItems = [
  { to: "/", key: "nav.home" },
  { to: "/about", key: "nav.about" },
  { to: "/services", key: "nav.services" },
  { to: "/industries", key: "nav.industries" },
  { to: "/amc-sla", key: "nav.amc" },
  { to: "/blog", key: "nav.blog" },
  { to: "/careers", key: "nav.careers" },
  { to: "/contact", key: "nav.contact" },
];

export const Header = () => {
  const { t, lang, toggleLang } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="container-orion flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img
            src={COMPANY.logo}
            alt="Orion"
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg object-cover bg-slate-950 shrink-0"
          />
          <div className="flex flex-col leading-none">
            <span className="font-heading text-base sm:text-lg font-semibold tracking-tight text-slate-900">
              {COMPANY.brand}
            </span>
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">
              {COMPANY.domain}
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.key.replace("nav.", "")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-blue-700" : "text-slate-700 hover:text-blue-700"
                }`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            aria-label="Toggle language"
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "العربية" : "English"}
          </button>
          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-slate-900 hover:bg-blue-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            {t("nav.getQuote")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="container-orion py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mnav-${item.key.replace("nav.", "")}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium rounded-md ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-700"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <button
              onClick={toggleLang}
              data-testid="mobile-lang-toggle"
              className="mt-2 flex items-center gap-2 px-3 py-3 text-base font-semibold text-slate-700"
            >
              <Globe className="w-5 h-5" /> {lang === "en" ? "العربية" : "English"}
            </button>
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-slate-900 text-white px-5 py-3 text-sm font-semibold"
            >
              {t("nav.getQuote")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
